import { useEffect, useMemo, useState } from "react";
import { X, Radio, HeartHandshake, Users, ExternalLink } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { supabase } from "../../../src/integrations/supabase/client";

export const PRAYER_CONFERENCE_ZOOM =
  "https://ofuesi.zoom.us/j/86166516167?pwd=S3ZZZ1pXcS8wMSsxbUVSb0FjZG5Pdz09";
export const WOMENS_MINISTRY_ZOOM =
  "https://ofuesi.zoom.us/j/89139800692?pwd=Z0RXQkVOSzdPR1RrdmxHSzI2b29Idz09";

type KitEvent = {
  region: string | null;
  start_date: string | null;
  end_date: string | null;
};

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const parseDay = (s?: string | null) => (s ? new Date(`${s}T00:00:00`) : null);

/** Nth weekday (0=Sun..6=Sat) of a month, e.g. 1st Saturday. */
const nthWeekday = (year: number, month: number, weekday: number, nth: number) => {
  const first = new Date(year, month, 1);
  const offset = (weekday - first.getDay() + 7) % 7;
  return new Date(year, month, 1 + offset + (nth - 1) * 7);
};

/** Next occurrence (today or later) of the nth weekday, rolling into next month. */
const nextNthWeekday = (weekday: number, nth: number) => {
  const today = startOfDay(new Date());
  const thisMonth = nthWeekday(today.getFullYear(), today.getMonth(), weekday, nth);
  if (thisMonth.getTime() >= today.getTime()) return thisMonth;
  return nthWeekday(today.getFullYear(), today.getMonth() + 1, weekday, nth);
};

const fmtDay = (d: Date) =>
  d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });

/**
 * Homepage ribbon: shows only what is timely — a regional conference that is
 * happening now or starts tomorrow, and the monthly Zoom gatherings on the day
 * before / day of the meeting.
 */
export const MinistryRibbon = () => {
  const [open, setOpen] = useState(true);
  const [events, setEvents] = useState<KitEvent[]>([]);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("conference_kit_events")
        .select("region, start_date, end_date");
      setEvents((data as KitEvent[]) || []);
    })();
  }, []);

  // Conferences live now, or starting within the next day.
  const activeConferences = useMemo(() => {
    const today = startOfDay(new Date()).getTime();
    return events
      .filter(
        (e) =>
          e.region &&
          e.start_date &&
          !/^south\s*west(?:\s+region)?$/i.test(e.region.trim()),
      )
      .map((e) => {
        const start = parseDay(e.start_date)!;
        const end = parseDay(e.end_date) || start;
        return { ...e, start, end };
      })
      .filter((e) => {
        const showFrom = e.start.getTime() - 86400000; // 1 day before
        return today >= showFrom && today <= e.end.getTime();
      })
      .sort((a, b) => a.start.getTime() - b.start.getTime())
      .map((e) => ({
        region: e.region as string,
        slug: (e.region as string).toLowerCase().replace(/[^a-z]/g, ""),
        live: today >= e.start.getTime(),
        start: e.start,
      }));
  }, [events]);

  const prayerDay = nextNthWeekday(6, 1); // 1st Saturday
  const womensDay = nextNthWeekday(6, 3); // 3rd Saturday
  const today = startOfDay(new Date()).getTime();
  const isNear = (d: Date) => d.getTime() - today <= 86400000 && d.getTime() >= today;
  const showPrayer = isNear(prayerDay);
  const showWomens = isNear(womensDay);

  const panels = activeConferences.length + (showPrayer ? 1 : 0) + (showWomens ? 1 : 0);
  if (!open || panels === 0) return null;

  const colClass = panels === 1 ? "md:grid-cols-1" : panels === 2 ? "md:grid-cols-2" : "md:grid-cols-3";

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -40, opacity: 0 }}
        className="relative z-40 shadow-md border-b border-white/10"
      >
        <div className={`grid grid-cols-1 ${colClass}`}>
          {activeConferences.map((c) => (
            <Link
              key={c.region}
              to={`/conference/${c.slug}`}
              className="group flex items-center justify-center gap-2 py-3 px-4 bg-gradient-to-r from-emerald-900 via-emerald-700 to-emerald-900 text-white transition hover:brightness-110"
            >
              <span
                className={`inline-flex items-center justify-center w-7 h-7 rounded-full bg-amber-400 text-emerald-950 shadow ring-2 ring-amber-200/60 shrink-0 ${c.live ? "animate-pulse" : ""}`}
              >
                <Radio className="w-4 h-4" />
              </span>
              <span className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 text-center">
                <span className="text-xs sm:text-sm font-bold uppercase tracking-wide text-amber-300">
                  {c.live
                    ? `${c.region} Conference is happening now`
                    : `${c.region} Conference starts tomorrow · ${fmtDay(c.start)}`}
                </span>
                <span className="inline-flex items-center gap-1 text-xs sm:text-sm font-semibold bg-amber-400 text-emerald-950 rounded-full px-2.5 py-0.5 group-hover:bg-amber-300 transition-colors">
                  View details
                </span>
              </span>
            </Link>
          ))}

          {showPrayer && (
            <a
              href={PRAYER_CONFERENCE_ZOOM}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center justify-center gap-2 py-3 px-4 bg-gradient-to-r from-sky-900 via-sky-700 to-sky-900 text-white transition hover:brightness-110"
            >
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-sky-200 text-sky-950 shadow ring-2 ring-sky-100/60 shrink-0">
                <HeartHandshake className="w-4 h-4" />
              </span>
              <span className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 text-center">
                <span className="text-xs sm:text-sm font-bold uppercase tracking-wide text-sky-200">
                  Monthly Prayer Conference · {fmtDay(prayerDay)}, 10:30 AM EST
                </span>
                <span className="inline-flex items-center gap-1 text-xs sm:text-sm font-semibold bg-sky-200 text-sky-950 rounded-full px-2.5 py-0.5 group-hover:bg-white transition-colors">
                  <ExternalLink className="w-3 h-3" /> Join now
                </span>
              </span>
            </a>
          )}

          {showWomens && (
            <a
              href={WOMENS_MINISTRY_ZOOM}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center justify-center gap-2 py-3 px-4 bg-gradient-to-r from-rose-900 via-rose-700 to-rose-900 text-white transition hover:brightness-110"
            >
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-rose-200 text-rose-950 shadow ring-2 ring-rose-100/60 shrink-0">
                <Users className="w-4 h-4" />
              </span>
              <span className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 text-center">
                <span className="text-xs sm:text-sm font-bold uppercase tracking-wide text-rose-200">
                  Women's Ministry Fellowship · {fmtDay(womensDay)} on Zoom
                </span>
                <span className="inline-flex items-center gap-1 text-xs sm:text-sm font-semibold bg-rose-200 text-rose-950 rounded-full px-2.5 py-0.5 group-hover:bg-white transition-colors">
                  <ExternalLink className="w-3 h-3" /> Join now
                </span>
              </span>
            </a>
          )}
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setOpen(false);
          }}
          className="absolute right-3 top-3 hover:bg-white/10 rounded-full p-1 z-10"
          aria-label="Dismiss announcements"
        >
          <X className="w-4 h-4 text-white" />
        </button>
      </motion.div>
    </AnimatePresence>
  );
};

export default MinistryRibbon;
